// Mock bridge for in-browser dev. Keychain-backed by localStorage (with a
// "sync" flag tucked into the key so get/set/delete behave distinctly for
// iCloud-synced vs local-only), StoreKit returns a fixed fake product,
// WhisperKit claims unavailable, haptics are no-ops.
//
// Intentionally minimal. If a test needs richer behavior (e.g. push event
// replay) the test can push events via bridge.emit() directly.

import type { Bridge } from './index'
import type { BridgeMethods, Product } from './types'
import { createEmitter } from './emitter'

const keychainKey = (key: string, sync: boolean) =>
  `mock.keychain.${sync ? 'sync' : 'local'}.${key}`

const fakeProPlan: Product = {
  id: 'v1',
  title: 'Hearth Pro',
  description: 'Push notifications, voice input, and temporary grants.',
  priceFormatted: '$4.99',
  priceAmount: 4.99,
  currencyCode: 'USD',
}

export function createMockBridge(): Bridge {
  const emitter = createEmitter()
  let subscribed = false

  const dispatch = async <K extends keyof BridgeMethods>(
    method: K,
    args: BridgeMethods[K]['args'],
  ): Promise<BridgeMethods[K]['result']> => {
    switch (method) {
      case 'keychain.get': {
        const a = args as BridgeMethods['keychain.get']['args']
        const value = localStorage.getItem(keychainKey(a.key, a.sync))
        return { value } as BridgeMethods[K]['result']
      }
      case 'keychain.set': {
        const a = args as BridgeMethods['keychain.set']['args']
        localStorage.setItem(keychainKey(a.key, a.sync), a.value)
        return {} as BridgeMethods[K]['result']
      }
      case 'keychain.delete': {
        const a = args as BridgeMethods['keychain.delete']['args']
        localStorage.removeItem(keychainKey(a.key, a.sync))
        return {} as BridgeMethods[K]['result']
      }
      case 'devicecheck.token':
        return {
          token: null,
          isSandbox: true,
          supported: false,
        } as BridgeMethods[K]['result']
      case 'playintegrity.token':
        return {
          token: null,
          supported: false,
        } as BridgeMethods[K]['result']
      case 'apns.registerForPush':
        return { authorized: false } as BridgeMethods[K]['result']
      case 'storekit.products':
        return { products: [fakeProPlan] } as BridgeMethods[K]['result']
      case 'storekit.purchase': {
        subscribed = true
        emitter.emit('subscription.updated', {
          subscribed: true,
          productID: 'v1',
          expiresAt: null,
        })
        return { outcome: 'success' } as BridgeMethods[K]['result']
      }
      case 'storekit.restore':
        return { restored: subscribed } as BridgeMethods[K]['result']
      case 'storekit.status':
        return {
          subscribed,
          productID: subscribed ? 'v1' : null,
          expiresAt: null,
        } as BridgeMethods[K]['result']
      case 'storekit.latestTransactionJWS':
        return { jws: null } as BridgeMethods[K]['result']
      case 'whisper.isAvailable':
        return {
          supported: false,
          ready: false,
          needsDownload: false,
          downloading: false,
          loading: false,
          reason: 'mock bridge — WhisperKit requires native',
        } as BridgeMethods[K]['result']
      case 'whisper.prepare':
        return {
          ready: false,
          error: 'mock bridge — WhisperKit requires native',
        } as BridgeMethods[K]['result']
      case 'whisper.startRecording':
      case 'whisper.stopRecording':
        return { transcript: '' } as BridgeMethods[K]['result']
      case 'voice.capabilities':
        // The browser has speechSynthesis, but reporting speak:true here would
        // make the dev bridge the only place voice UI appears without a shell
        // to drive it. Report honestly instead: nothing, and say why.
        return {
          speak: false,
          session: false,
          onDeviceRecognition: false,
          reason: 'mock bridge — voice requires a native shell',
        } as BridgeMethods[K]['result']
      case 'voice.speak':
        return { speaking: false } as BridgeMethods[K]['result']
      case 'voice.stopSpeaking':
      case 'voice.setKeepAwake':
      case 'voice.endSession':
        return {} as BridgeMethods[K]['result']
      case 'voice.startSession':
        return { started: false } as BridgeMethods[K]['result']
      case 'haptic.light':
      case 'haptic.success':
      case 'haptic.warning':
      case 'bridge.ready':
      case 'notifications.reconcile':
        // No native NotificationCenter in the dev/web bridge; the call
        // is a no-op here. Real platforms (iOS) implement it in their
        // shell.
        return {} as BridgeMethods[K]['result']
      case 'notifications.clearForTarget':
        // No native tray in the mock bridge; distinct case because its
        // result carries a `removed` count.
        return { removed: 0 } as BridgeMethods[K]['result']
      case 'app.info':
        return {
          version: '0.0.0-dev',
          build: 'mock',
          gitSHA: 'mock',
          bundleID: 'com.vergelabs.hearthcmd.mock',
          buildConfig: 'debug',
          platform: 'ios',
        } as BridgeMethods[K]['result']
      case 'app.openURL':
        window.open((args as { url: string }).url, '_blank', 'noopener')
        return { ok: true } as BridgeMethods[K]['result']

      case 'clipboard.write': {
        // No pasteboard scoping to be had outside a native shell, so this
        // tier is just the async Clipboard API. It needs a secure context
        // and transient user activation; util/clipboard.ts owns the
        // execCommand fallback for when it has neither.
        const text = (args as { text: string }).text
        if (!navigator.clipboard?.writeText) {
          return { ok: false } as BridgeMethods[K]['result']
        }
        await navigator.clipboard.writeText(text)
        return { ok: true } as BridgeMethods[K]['result']
      }

      case 'files.open':
        // No OS file handoff in the mock bridge; the web viewer keys off
        // isNative (false here) and shows a download link instead.
        return { ok: false } as BridgeMethods[K]['result']
      default: {
        const _exhaustive: never = method
        throw new Error(`mock bridge: unknown method ${String(_exhaustive)}`)
      }
    }
  }

  return {
    isNative: false,
    isAndroid: false,
    call(method, args) {
      return dispatch(method, args ?? ({} as never))
    },
    on: emitter.on,
    emit: emitter.emit,
  }
}
